import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { adminAPI } from '../services/api';
import { toast } from 'sonner';
import {
  Calendar,
  Users,
  MapPin,
  Mail,
  Phone,
  Loader2,
  IndianRupee,
  AlertTriangle,
  CheckCircle2,
  Clock,
  XCircle,
  Search,
  RefreshCw,
  Download,
} from 'lucide-react';

const STATUS_OPTIONS = [
  { value: 'all', label: 'All statuses' },
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'pending', label: 'Pending payment' },
  { value: 'cancellation_requested', label: 'Cancellation requested' },
  { value: 'cancelled', label: 'Cancelled' },
];

const DATE_FIELD_OPTIONS = [
  { value: 'check_in', label: 'Check-in date' },
  { value: 'created_at', label: 'Booking date' },
];

const RANGE_PRESETS = [
  { value: 'all', label: 'All time' },
  { value: 'last7', label: 'Last 7 days' },
  { value: 'last30', label: 'Last 30 days' },
  { value: 'last90', label: 'Last 90 days' },
  { value: 'this_month', label: 'This month' },
  { value: 'last_month', label: 'Last month' },
  { value: 'custom', label: 'Custom range' },
];

const toYMD = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const computeRange = (preset) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  switch (preset) {
    case 'last7': {
      const from = new Date(today); from.setDate(today.getDate() - 6);
      return { date_from: toYMD(from), date_to: toYMD(today) };
    }
    case 'last30': {
      const from = new Date(today); from.setDate(today.getDate() - 29);
      return { date_from: toYMD(from), date_to: toYMD(today) };
    }
    case 'last90': {
      const from = new Date(today); from.setDate(today.getDate() - 89);
      return { date_from: toYMD(from), date_to: toYMD(today) };
    }
    case 'this_month': {
      const from = new Date(today.getFullYear(), today.getMonth(), 1);
      const to = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      return { date_from: toYMD(from), date_to: toYMD(to) };
    }
    case 'last_month': {
      const from = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const to = new Date(today.getFullYear(), today.getMonth(), 0);
      return { date_from: toYMD(from), date_to: toYMD(to) };
    }
    default:
      return { date_from: '', date_to: '' };
  }
};

const statusBadge = (status) => {
  switch (status) {
    case 'confirmed':
      return { className: 'bg-green-100 text-green-700', icon: CheckCircle2, label: 'Confirmed' };
    case 'pending':
      return { className: 'bg-yellow-100 text-yellow-700', icon: Clock, label: 'Pending' };
    case 'cancellation_requested':
      return { className: 'bg-orange-100 text-orange-700', icon: AlertTriangle, label: 'Cancellation Requested' };
    case 'cancelled':
      return { className: 'bg-red-100 text-red-700', icon: XCircle, label: 'Cancelled' };
    default:
      return { className: 'bg-gray-100 text-gray-700', icon: Clock, label: status || 'Unknown' };
  }
};

const cancelBadge = (status) => {
  switch (status) {
    case 'pending':
      return 'bg-orange-100 text-orange-700';
    case 'accepted':
      return 'bg-green-100 text-green-700';
    case 'rejected':
      return 'bg-red-100 text-red-700';
    default:
      return 'bg-gray-100 text-gray-700';
  }
};

// CSV building utilities ---------------------------------------------------
const csvEscape = (val) => {
  if (val === null || val === undefined) return '';
  const s = String(val);
  if (/[",\n\r]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
};

const CSV_HEADERS = [
  'Booking ID',
  'Booking Status',
  'Payment Status',
  'Property',
  'Location',
  'Host Name',
  'Host Email',
  'Guest Name',
  'Guest Email',
  'Guest Phone',
  'Check-in',
  'Check-out',
  'Nights',
  'Guests',
  'Price/Night (INR)',
  'Subtotal (INR)',
  'Total (INR)',
  'Currency',
  'Razorpay Order ID',
  'Razorpay Payment ID',
  'Special Requests',
  'Booking Created At',
  'Cancellation Status',
  'Cancellation Reason',
  'Host Response',
  'Cancellation Requested At',
];

const bookingToRow = (b) => [
  b.id,
  b.booking_status,
  b.payment_status,
  b.property_title,
  b.property_location,
  b.host_name,
  b.host_email,
  b.guest_name,
  b.guest_email,
  b.guest_phone,
  b.check_in,
  b.check_out,
  b.nights,
  b.guests,
  b.price_per_night,
  b.subtotal,
  b.total_amount,
  b.currency || 'INR',
  b.razorpay_order_id,
  b.razorpay_payment_id,
  b.special_requests,
  b.created_at,
  b.cancellation?.status,
  b.cancellation?.reason,
  b.cancellation?.host_message,
  b.cancellation?.created_at,
];

const downloadCSV = (rows, filename) => {
  const lines = [
    CSV_HEADERS.map(csvEscape).join(','),
    ...rows.map((r) => r.map(csvEscape).join(',')),
  ];
  const blob = new Blob(['\ufeff' + lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

// --------------------------------------------------------------------------

const AdminBookingsTab = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [propertyOptions, setPropertyOptions] = useState([]);
  const [stats, setStats] = useState({});
  const [filters, setFilters] = useState({ status: 'all', property_id: 'all' });
  const [search, setSearch] = useState('');
  const [datePreset, setDatePreset] = useState('all');
  const [dateField, setDateField] = useState('check_in');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [exporting, setExporting] = useState(false);

  const activeDateRange = useMemo(() => {
    if (datePreset === 'all') return { date_from: '', date_to: '' };
    if (datePreset === 'custom') return { date_from: customFrom, date_to: customTo };
    return computeRange(datePreset);
  }, [datePreset, customFrom, customTo]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const apiFilters = {};
      if (filters.status !== 'all') apiFilters.status = filters.status;
      if (filters.property_id !== 'all') apiFilters.property_id = filters.property_id;
      if (activeDateRange.date_from) apiFilters.date_from = activeDateRange.date_from;
      if (activeDateRange.date_to) apiFilters.date_to = activeDateRange.date_to;
      if (activeDateRange.date_from || activeDateRange.date_to) apiFilters.date_field = dateField;
      const response = await adminAPI.getBookings(apiFilters);
      if (response.success) {
        setData(response.data || []);
        setPropertyOptions(response.properties || []);
        setStats(response.stats || {});
      }
    } catch (e) {
      toast.error('Failed to load bookings');
    } finally {
      setLoading(false);
    }
  }, [filters.status, filters.property_id, activeDateRange.date_from, activeDateRange.date_to, dateField]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    if (!search.trim()) return data;
    const q = search.trim().toLowerCase();
    return data.filter((b) =>
      (b.guest_name || '').toLowerCase().includes(q) ||
      (b.guest_email || '').toLowerCase().includes(q) ||
      (b.property_title || '').toLowerCase().includes(q) ||
      (b.property_location || '').toLowerCase().includes(q) ||
      (b.id || '').toLowerCase().includes(q)
    );
  }, [data, search]);

  // Group bookings by property
  const groupedByProperty = useMemo(() => {
    const map = new Map();
    for (const b of filtered) {
      const key = b.property_id || 'unknown';
      if (!map.has(key)) {
        map.set(key, {
          property_id: key,
          property_title: b.property_title || 'Unknown property',
          property_location: b.property_location || '',
          host_name: b.host_name || '',
          bookings: [],
        });
      }
      map.get(key).bookings.push(b);
    }
    return Array.from(map.values());
  }, [filtered]);

  // Revenue total for the filtered set (confirmed + paid)
  const filteredRevenue = useMemo(() => {
    return filtered
      .filter((b) => b.booking_status === 'confirmed' || b.payment_status === 'completed')
      .reduce((sum, b) => sum + (b.total_amount || 0), 0);
  }, [filtered]);

  const handleExport = () => {
    if (filtered.length === 0) {
      toast.error('Nothing to export — current filters return no bookings');
      return;
    }
    setExporting(true);
    try {
      const rows = filtered.map(bookingToRow);
      const parts = ['geostays_bookings'];
      if (activeDateRange.date_from || activeDateRange.date_to) {
        parts.push(`${activeDateRange.date_from || 'start'}_to_${activeDateRange.date_to || 'end'}`);
      } else {
        parts.push(toYMD(new Date()));
      }
      if (filters.status !== 'all') parts.push(filters.status);
      const filename = `${parts.join('_')}.csv`;
      downloadCSV(rows, filename);
      toast.success(`Exported ${rows.length} booking${rows.length > 1 ? 's' : ''} to ${filename}`);
    } finally {
      setExporting(false);
    }
  };

  const clearAll = () => {
    setSearch('');
    setFilters({ status: 'all', property_id: 'all' });
    setDatePreset('all');
    setCustomFrom('');
    setCustomTo('');
    setDateField('check_in');
  };

  return (
    <div className="space-y-6" data-testid="admin-bookings-tab">
      {/* Stats strip */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        <StatCard label="Total" value={stats.total ?? 0} color="bg-gray-100 text-gray-700" />
        <StatCard label="Confirmed" value={stats.confirmed ?? 0} color="bg-green-100 text-green-700" />
        <StatCard label="Pending" value={stats.pending ?? 0} color="bg-yellow-100 text-yellow-700" />
        <StatCard label="Cancel Req." value={stats.cancellation_requested ?? 0} color="bg-orange-100 text-orange-700" />
        <StatCard label="Cancelled" value={stats.cancelled ?? 0} color="bg-red-100 text-red-700" />
        <StatCard
          label="Revenue (INR)"
          value={(stats.total_revenue ?? 0).toLocaleString()}
          color="bg-[#7CB342]/10 text-[#5a8a2b]"
        />
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4 space-y-3">
          {/* Row 1: Search + Property + Status + Clear */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
            <div>
              <label className="text-xs font-medium text-gray-500 mb-1 block">Search</label>
              <div className="relative">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Guest, email, property, booking id..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-8"
                  data-testid="admin-bookings-search"
                />
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500 mb-1 block">Property</label>
              <Select
                value={filters.property_id}
                onValueChange={(v) => setFilters((f) => ({ ...f, property_id: v }))}
              >
                <SelectTrigger data-testid="admin-bookings-property-filter">
                  <SelectValue placeholder="All properties" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All properties</SelectItem>
                  {propertyOptions.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.title || p.id}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500 mb-1 block">Status</label>
              <Select
                value={filters.status}
                onValueChange={(v) => setFilters((f) => ({ ...f, status: v }))}
              >
                <SelectTrigger data-testid="admin-bookings-status-filter">
                  <SelectValue placeholder="All statuses" />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map((s) => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Button
                variant="outline"
                onClick={clearAll}
                className="w-full"
                data-testid="admin-bookings-clear-filters"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Clear filters
              </Button>
            </div>
          </div>

          {/* Row 2: Date range + date field + Export */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end pt-2 border-t">
            <div>
              <label className="text-xs font-medium text-gray-500 mb-1 block">Date range</label>
              <Select value={datePreset} onValueChange={setDatePreset}>
                <SelectTrigger data-testid="admin-bookings-date-preset">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {RANGE_PRESETS.map((p) => (
                    <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500 mb-1 block">Apply range to</label>
              <Select
                value={dateField}
                onValueChange={setDateField}
                disabled={datePreset === 'all'}
              >
                <SelectTrigger data-testid="admin-bookings-date-field">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DATE_FIELD_OPTIONS.map((d) => (
                    <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {datePreset === 'custom' ? (
              <>
                <div>
                  <label className="text-xs font-medium text-gray-500 mb-1 block">From</label>
                  <Input
                    type="date"
                    value={customFrom}
                    onChange={(e) => setCustomFrom(e.target.value)}
                    data-testid="admin-bookings-custom-from"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500 mb-1 block">To</label>
                  <Input
                    type="date"
                    value={customTo}
                    onChange={(e) => setCustomTo(e.target.value)}
                    data-testid="admin-bookings-custom-to"
                  />
                </div>
              </>
            ) : (
              <div className="md:col-span-2 flex items-end">
                {activeDateRange.date_from && activeDateRange.date_to && (
                  <p className="text-xs text-gray-500">
                    Showing {activeDateRange.date_from} → {activeDateRange.date_to}
                  </p>
                )}
              </div>
            )}
            <div>
              <Button
                onClick={handleExport}
                disabled={exporting || filtered.length === 0}
                className="w-full bg-[#7CB342] hover:bg-[#6da036] text-white"
                data-testid="admin-bookings-export-csv"
              >
                {exporting ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Download className="h-4 w-4 mr-2" />
                )}
                Export CSV ({filtered.length})
              </Button>
            </div>
          </div>

          {/* Summary row */}
          <div className="flex items-center justify-between pt-2 border-t text-xs text-gray-500">
            <span>
              Showing <strong className="text-gray-800">{filtered.length}</strong> of {data.length} loaded
            </span>
            <span>
              Filtered revenue (confirmed/paid):{' '}
              <strong className="text-[#5a8a2b]">₹{filteredRevenue.toLocaleString()}</strong>
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Results */}
      {loading ? (
        <div className="text-center py-20">
          <Loader2 className="h-10 w-10 animate-spin text-[#7CB342] mx-auto" />
          <p className="mt-3 text-gray-500">Loading bookings...</p>
        </div>
      ) : filtered.length === 0 ? (
        <Card>
          <CardContent className="py-20 text-center">
            <Calendar className="h-16 w-16 text-gray-300 mx-auto mb-3" />
            <h3 className="text-xl font-semibold text-gray-900 mb-1">No bookings found</h3>
            <p className="text-gray-500">Try adjusting filters or search.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {groupedByProperty.map((group) => (
            <Card key={group.property_id} className="overflow-hidden" data-testid={`property-bookings-group-${group.property_id}`}>
              <div className="bg-gray-50 border-b px-4 py-3 flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-gray-900">{group.property_title}</h3>
                  <p className="text-xs text-gray-500 flex items-center gap-1">
                    <MapPin className="h-3 w-3" />
                    {group.property_location}
                    {group.host_name ? <> · Host: {group.host_name}</> : null}
                  </p>
                </div>
                <Badge className="bg-white border text-gray-700">
                  {group.bookings.length} booking{group.bookings.length > 1 ? 's' : ''}
                </Badge>
              </div>
              <CardContent className="p-0">
                <div className="divide-y">
                  {group.bookings.map((b) => {
                    const sb = statusBadge(b.booking_status);
                    const Icon = sb.icon;
                    return (
                      <div key={b.id} className="p-4" data-testid={`admin-booking-row-${b.id}`}>
                        <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                          <div className="min-w-0">
                            <p className="text-xs text-gray-400">Booking ID: <span className="font-mono">{b.id}</span></p>
                            <div className="flex items-center gap-2 mt-1 flex-wrap">
                              <Users className="h-4 w-4 text-gray-400" />
                              <span className="font-medium text-gray-900">{b.guest_name}</span>
                              <span className="text-gray-400">·</span>
                              <a href={`mailto:${b.guest_email}`} className="text-sm text-[#7CB342] hover:underline flex items-center gap-1">
                                <Mail className="h-3.5 w-3.5" /> {b.guest_email}
                              </a>
                              {b.guest_phone && (
                                <>
                                  <span className="text-gray-400">·</span>
                                  <a href={`tel:${b.guest_phone}`} className="text-sm text-[#7CB342] hover:underline flex items-center gap-1">
                                    <Phone className="h-3.5 w-3.5" /> {b.guest_phone}
                                  </a>
                                </>
                              )}
                            </div>
                          </div>
                          <Badge className={sb.className}>
                            <Icon className="h-3 w-3 mr-1" />
                            {sb.label}
                          </Badge>
                        </div>

                        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-sm">
                          <Info label="Check-in" value={b.check_in} />
                          <Info label="Check-out" value={b.check_out} />
                          <Info label="Nights" value={b.nights} />
                          <Info label="Guests" value={b.guests} />
                          <Info
                            label="Total"
                            value={
                              <span className="font-semibold text-[#7CB342] flex items-center">
                                <IndianRupee className="h-3 w-3" />
                                {(b.total_amount || 0).toLocaleString()}
                              </span>
                            }
                          />
                        </div>

                        {b.special_requests && (
                          <div className="mt-3 text-sm text-gray-600">
                            <span className="text-gray-400">Special requests: </span>
                            {b.special_requests}
                          </div>
                        )}

                        {/* Cancellation info */}
                        {b.cancellation && (
                          <div className="mt-3 border rounded-md p-3 bg-orange-50/40">
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-2 text-sm font-medium text-gray-800">
                                <AlertTriangle className="h-4 w-4 text-orange-500" />
                                Cancellation
                              </div>
                              <Badge className={cancelBadge(b.cancellation.status)}>
                                {b.cancellation.status.charAt(0).toUpperCase() + b.cancellation.status.slice(1)}
                              </Badge>
                            </div>
                            <div className="text-sm">
                              <p><span className="text-gray-500">Reason:</span> {b.cancellation.reason || '—'}</p>
                              {b.cancellation.host_message && (
                                <p className="mt-1"><span className="text-gray-500">Host response:</span> {b.cancellation.host_message}</p>
                              )}
                              {b.cancellation.created_at && (
                                <p className="text-xs text-gray-400 mt-1">
                                  Requested on {new Date(b.cancellation.created_at).toLocaleString()}
                                </p>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Payment meta */}
                        <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-gray-500">
                          {b.payment_status && (
                            <span>Payment: <strong className="text-gray-700">{b.payment_status}</strong></span>
                          )}
                          {b.razorpay_order_id && (
                            <span className="font-mono">Order: {b.razorpay_order_id}</span>
                          )}
                          {b.razorpay_payment_id && (
                            <span className="font-mono">Payment: {b.razorpay_payment_id}</span>
                          )}
                          {b.created_at && (
                            <span>Created: {new Date(b.created_at).toLocaleString()}</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

const StatCard = ({ label, value, color }) => (
  <div className={`rounded-lg px-3 py-2 ${color}`}>
    <div className="text-xs uppercase tracking-wide opacity-70">{label}</div>
    <div className="text-xl font-bold leading-tight">{value}</div>
  </div>
);

const Info = ({ label, value }) => (
  <div>
    <p className="text-xs text-gray-400">{label}</p>
    <p className="font-medium text-gray-900">{value}</p>
  </div>
);

export default AdminBookingsTab;
